import React from "react";
import styled from 'styled-components';

import { LabeledData } from "./LabeledData";
import { LabeledDataRow } from "./LabeledDataRow";
import { Paragraph, ParagraphSmall } from "../theme/typography";
import { UnorderedList, UnorderedListItem } from "../theme/typography";
import { Tab } from "./Tab";
import { TabGroup } from "./TabGroup";

const specificationVersions = [
	{ version: "2.5", material: "T700 carbon fiber", materialNote: "Plain weave, matte finish. Pro material variant available (the strongest so far)." },
	{ version: "2.4", material: "T700 carbon fiber" },
	{ version: "1.12", material: "T300 carbon fiber" },
];

const ContentContainer = styled.div`
	box-sizing: border-box;

	${LabeledDataRow} {
		margin-top: 2rem;
	}

	${LabeledDataRow}:first-child {
		margin-top: 0;
	}
`;

const ContentLayout = styled.div`
	box-sizing: border-box;
	max-width: 32rem;
	margin: 0 auto;
	padding: 1.75rem 1.5rem;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.third}) {
		max-width: 64rem;
		margin: 0 auto;
		padding: 2rem;
	}
`;

const SpecificationColumns = styled.div`
	display: grid;
	gap: 2rem;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.third}) {
		gap: 4rem;
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
`;

const StyledServalSpecBlock = styled.section`
	background: ${(props) => props.theme.colors.neutral10};
	color: ${(props) => props.theme.colors.neutral2};
`;

const Title = styled.h3`
	${(props) => props.theme.typography.headingL};
	margin-top: 0;
`;

const Info = styled.h3`
	${(props) => props.theme.typography.headingS};
	margin-bottom: 0.5rem;
	margin-top: 2rem;
`;

const Example = styled(ParagraphSmall)`
	color: ${(props) => props.theme.colors.neutral4};
	margin-top: 0.5rem;
`;

const Heading = styled.h3`
	${(props) => props.theme.typography.headingS};
	margin-top: 0;
	margin-bottom: 1rem;
`;

const PackageItemParagraph = styled(Paragraph)`
	margin-bottom: 0;
`;

const PackageItemParagraphSmall = styled(ParagraphSmall)`
	color: ${(props) => props.theme.colors.neutral4};
	margin-bottom: 0;
	margin-top: 0.25rem;
`;

export class ServalSpecBlock extends React.Component {
	constructor(props) {
		super(props);
		this.state = { activeVersion: "2.5" };
	}

	handleTabKeyDown = (event, index) => {
		let nextIndex;
		if (event.key === "ArrowRight") nextIndex = (index + 1) % specificationVersions.length;
		else if (event.key === "ArrowLeft") nextIndex = (index - 1 + specificationVersions.length) % specificationVersions.length;
		else if (event.key === "Home") nextIndex = 0;
		else if (event.key === "End") nextIndex = specificationVersions.length - 1;
		else return;

		event.preventDefault();
		const nextVersion = specificationVersions[nextIndex].version;
		this.setState({ activeVersion: nextVersion }, () => {
			document.getElementById(`serval-spec-tab-${nextVersion}`)?.focus();
		});
	};

	render() {
		const { activeVersion } = this.state;
		const activeSpecification = specificationVersions.find(({ version }) => version === activeVersion);

		return (
			<StyledServalSpecBlock>
				<ContentLayout>
					<Title>Specifications</Title>
					<TabGroup aria-label="Serval version specifications">
						{specificationVersions.map(({ version }, index) => (
							<Tab
								key={version}
								id={`serval-spec-tab-${version}`}
								role="tab"
								aria-selected={activeVersion === version}
								aria-controls={`serval-spec-panel-${version}`}
								tabIndex={activeVersion === version ? 0 : -1}
								active={activeVersion === version}
								onClick={() => this.setState({ activeVersion: version })}
								onKeyDown={(event) => this.handleTabKeyDown(event, index)}
							>
								{version}
							</Tab>
						))}
					</TabGroup>
					<div
						id={`serval-spec-panel-${activeVersion}`}
						role="tabpanel"
						aria-labelledby={`serval-spec-tab-${activeVersion}`}
						tabIndex="0"
					>
						<SpecificationColumns>
							<ContentContainer>
								<LabeledDataRow>
									<LabeledData
										size="m"
										colorContext="light"
										label="Weight"
										data="~56g"
										supplemental="Frame kit without TPU parts and stack screws."
									/>
								</LabeledDataRow>
								<LabeledDataRow>
									<LabeledData
										size="m"
										colorContext="light"
										label="Material"
										data={activeSpecification.material}
										supplemental={activeSpecification.materialNote || 'Plain weave, matte finish.'}
									/>
								</LabeledDataRow>
								<LabeledDataRow>
									<LabeledData
										size="m"
										colorContext="light"
										label="Stack mounting"
										data="20x20"
									/>
									<LabeledData
										size="m"
										colorContext="light"
										label="Motor mounting"
										data="16 mm"
									/>
								</LabeledDataRow>
								<LabeledDataRow>
									<LabeledData
										size="m"
										colorContext="light"
										label="Stack height"
										data="23 mm"
									/>
								</LabeledDataRow>
								<LabeledDataRow>
									<LabeledData
										size="m"
										colorContext="light"
										label="Arm thickness"
										data="5 mm"
									/>
									<LabeledData
										size="m"
										colorContext="light"
										label="Plate thickness"
										data="2 mm"
									/>
								</LabeledDataRow>
								<Info>Fits most 20x20 ESCs</Info>
								<Example>For example: Foxeer Reaper Slim 60A, Flycolor Trinx G20, Diatone Mamba F65_128K Mini, Diatone Mamba 5 Reactor_66.</Example>
								<Info>Fits HDZero Nano 90, and most Nano analog cameras</Info>
								<Example>For example: Foxeer Predator Nano.</Example>
							</ContentContainer>
							<ContentContainer>
								<Heading>Frame kit contains</Heading>
								<UnorderedList>
									<UnorderedListItem>
										<PackageItemParagraph>4x Arms</PackageItemParagraph>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>1x Mid plate</PackageItemParagraph>
										<PackageItemParagraphSmall>with 6x M3 black carbon steel pressnuts, pre-installed</PackageItemParagraphSmall>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>1x Bottom plate</PackageItemParagraph>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>1x Top plate</PackageItemParagraph>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>2x Stiffeners</PackageItemParagraph>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>4x 25mm black aluminium standoffs</PackageItemParagraph>
										<PackageItemParagraphSmall>Gold colored standoffs also available upon request</PackageItemParagraphSmall>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>4x M3x25mm countersunk stack screws</PackageItemParagraph>
										<PackageItemParagraphSmall>Grade 12.9 black carbon steel</PackageItemParagraphSmall>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>4x M3 steel/nylon hex self-locking nuts</PackageItemParagraph>
										<PackageItemParagraphSmall>Weight: 1.66g</PackageItemParagraphSmall>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>4x M3 nylon nuts</PackageItemParagraph>
										<PackageItemParagraphSmall>Weight: 0.22g</PackageItemParagraphSmall>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>4x M3x16mm countersunk arm screws</PackageItemParagraph>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>2x M3x12mm countersunk center screws</PackageItemParagraph>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>4x M3x6mm top plate screws</PackageItemParagraph>
									</UnorderedListItem>
									<UnorderedListItem>
										<PackageItemParagraph>Decorative leaflet with build tips and assembly diagram.</PackageItemParagraph>
									</UnorderedListItem>
								</UnorderedList>
								<Heading>Arm kit contains</Heading>
								<UnorderedList>
									<UnorderedListItem>
										<PackageItemParagraph>4x Arms</PackageItemParagraph>
									</UnorderedListItem>
								</UnorderedList>
								<Heading>Side brace kit contains</Heading>
								<UnorderedList>
									<UnorderedListItem>
										<PackageItemParagraph>2x Side braces</PackageItemParagraph>
									</UnorderedListItem>
								</UnorderedList>
								<Heading>Front/back brace kit contains</Heading>
								<UnorderedList>
									<UnorderedListItem>
										<PackageItemParagraph>2x Front/back braces</PackageItemParagraph>
									</UnorderedListItem>
								</UnorderedList>
							</ContentContainer>
						</SpecificationColumns>
					</div>
				</ContentLayout>
			</StyledServalSpecBlock>
		);
	}
}